import { api } from "@/lib/api";
import { toast } from "sonner";

async function loadMediaBlob(url, onProgress) {
    if (url.startsWith("http")) {
      try {
        const r = await fetch(url);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return await r.blob();
      } catch {
        const { data } = await api.get("/media-proxy", {
          params: { url }, responseType: "blob", timeout: 600000,
          onDownloadProgress: (e) => { if (e.total) onProgress?.(Math.round((e.loaded / e.total) * 100)); },
        });
        return data;
      }
    } else {
      const { data } = await api.get(url.replace(/^\/api/, ""), {
        responseType: "blob", timeout: 600000,
        onDownloadProgress: (e) => { if (e.total) onProgress?.(Math.round((e.loaded / e.total) * 100)); },
      });
      return data;
    }
}

// Robuster Medien-Download: Handy → Teilen-Menü (App bleibt offen), Desktop → Blob-Download
export async function downloadMediaFile({ url, title, onProgress }) {
  let blob;
  try {
    blob = await loadMediaBlob(url, onProgress);
  } catch (err) {
    let detail = "";
    if (err?.response?.data instanceof Blob) {
      try { detail = JSON.parse(await err.response.data.text())?.detail || ""; } catch { /* noop */ }
    }
    const status = err?.response?.status ? ` (${err.response.status})` : "";
    const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone;
    if (url.startsWith("http") && !standalone) {
      window.open(url, "_blank", "noopener");
      toast.info("Direkter Download blockiert — Datei wurde in neuem Tab geöffnet");
    } else {
      toast.error(`Download fehlgeschlagen${status}${detail ? ` — ${detail}` : ""}`);
    }
    return false;
  }
  return saveMediaBlob(blob, mediaFileName(title, blob.type));
}

function mediaFileName(title, type) {
  const mime = (type || "").split(";")[0].toLowerCase();
  const ext = ({ "image/jpeg": "jpg", "video/quicktime": "mov", "application/zip": "zip" })[mime]
    || mime.split("/")[1] || "bin";
  const hasExt = /\.(jpg|jpeg|png|gif|webp|heic|mp4|mov|webm|pdf|zip|mp3|wav|bin)$/i.test(title || "");
  return hasExt
    ? (title || "").replace(/[^\wäöüÄÖÜß. -]/g, "").slice(0, 80)
    : `${(title || "medium").replace(/[^\wäöüÄÖÜß -]/g, "").slice(0, 60) || "medium"}.${ext}`;
}

async function saveMediaBlob(blob, name) {
  const saveViaAnchor = () => {
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(u), 30000);
    toast.success(`„${name}“ heruntergeladen ✅`);
  };
  const isMobile = window.matchMedia?.("(pointer: coarse)")?.matches;
  try {
    const file = new File([blob], name, { type: blob.type || "application/octet-stream" });
    if (isMobile && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file] });
        toast.success(blob.type === "application/zip" ? "ZIP im Teilen-Menü unter »In Dateien sichern« ablegen" : "Im Teilen-Menü »Sichern«/»In Fotos sichern« wählen ✅");
      } catch (err) {
        if (err?.name !== "AbortError") saveViaAnchor();
      }
    } else {
      saveViaAnchor();
    }
  } catch {
    saveViaAnchor();
  }
  return true;
}

export function mediaDownloadUrls(media) {
  // Karussell-Reihenfolge erhalten; ein identisches Vorschaubild nicht doppelt.
  return [...new Set([...(media.carousel_urls || []), media.video_url, media.media_url].filter(Boolean))];
}

export async function createMediaZip(files) {
  // Originale unverändert speichern. Streaming CRC/ZIP statt große Videos
  // vollständig in JavaScript-ArrayBuffer zu kopieren oder erneut zu komprimieren.
  const { Zip, ZipPassThrough } = await import("fflate");
  const parts = [];
  let failure;
  const zip = new Zip((error, data) => {
    if (error) failure = error;
    else parts.push(new Blob([data]));
  });
  for (const { name, blob } of files) {
    const entry = new ZipPassThrough(name);
    zip.add(entry);
    const reader = blob.stream().getReader();
    try {
      while (true) {
        const { value, done } = await reader.read();
        entry.push(value || new Uint8Array(), done);
        if (failure) throw failure;
        if (done) break;
      }
    } finally { reader.releaseLock(); }
  }
  zip.end();
  if (failure) throw failure;
  return new Blob(parts, { type: "application/zip" });
}

export async function downloadMediaFiles({ urls, title, onProgress }) {
  const unique = [...new Set(urls.filter(Boolean))];
  if (unique.length === 1) return downloadMediaFile({ url: unique[0], title, onProgress });
  if (!unique.length) return false;
  try {
    const files = [];
    let total = 0;
    for (const [index, url] of unique.entries()) {
      const blob = await loadMediaBlob(url, (pct) => onProgress?.(Math.round((index + pct / 100) / unique.length * 85)));
      if (!blob.size || /^(text\/html|application\/json)(;|$)/i.test(blob.type)) {
        throw new Error(`Medium ${index + 1} konnte nicht als Datei geladen werden`);
      }
      total += blob.size;
      if (total > 2 * 1024 * 1024 * 1024) throw new Error("Die Medien sind zusammen größer als 2 GB. Bitte einzeln herunterladen.");
      files.push({ name: `${String(index + 1).padStart(2, "0")}_${mediaFileName(title, blob.type)}`, blob });
      onProgress?.(Math.round((index + 1) / unique.length * 85));
    }
    const zip = await createMediaZip(files);
    onProgress?.(100);
    const base = title || "Freigabe";
    return await saveMediaBlob(zip, mediaFileName(`${base} - alle Medien`, "application/zip"));
  } catch (error) {
    toast.error(`Sammeldownload fehlgeschlagen — ${error?.message || "bitte erneut versuchen"}. Es wurde kein unvollständiges ZIP heruntergeladen.`);
    return false;
  }
}
